import { useEffect, useState } from 'react';
import { AnimatePresence, motion, useMotionValue, useTransform } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../components/Icon';
import { CardGrid, useProfileActions } from '../components/ProfileCard';
import { EmptyState, MatchRing, Page, PageHead, Photo, SkeletonGrid, Tabs } from '../components/UI';
import { api } from '../utils/api';
import { cmToFeet } from '../utils/format';

function SwipeCard({ p, onDecide, top }) {
  const navigate = useNavigate();
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-250, 250], [-14, 14]);
  const likeOpacity = useTransform(x, [30, 130], [0, 1]);
  const nopeOpacity = useTransform(x, [-130, -30], [1, 0]);
  return (
    <motion.div
      className="swipe__card"
      style={{ x, rotate, zIndex: top ? 2 : 1 }}
      drag={top ? 'x' : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.9}
      onDragEnd={(e, info) => {
        if (info.offset.x > 120) onDecide('like');
        else if (info.offset.x < -120) onDecide('skip');
      }}
      initial={{ scale: 0.94, y: 18, opacity: 0 }}
      animate={{ scale: top ? 1 : 0.94, y: top ? 0 : 18, opacity: 1 }}
      exit={(dir) => ({ x: dir === 'like' ? 500 : dir === 'skip' ? -500 : 0, y: dir === 'star' ? -500 : 0, opacity: 0, rotate: dir === 'like' ? 20 : dir === 'skip' ? -20 : 0, transition: { duration: 0.35 } })}
      transition={{ type: 'spring', stiffness: 260, damping: 26 }}
    >
      <Photo src={p.photo} alt={p.name} locked={p.photoLocked} />
      <motion.span className="swipe__stamp swipe__stamp--like" style={{ opacity: likeOpacity }}>INTEREST</motion.span>
      <motion.span className="swipe__stamp swipe__stamp--nope" style={{ opacity: nopeOpacity }}>SKIP</motion.span>
      <div className="swipe__info">
        <div className="swipe__row">
          <div>
            <h2>
              {p.name}, {p.age} {p.verified && <Icon name="shield" size={18} className="verified" />}
            </h2>
            <p>{[cmToFeet(p.heightCm), p.religion, p.motherTongue].filter(Boolean).join(' · ')}</p>
            <p><Icon name="briefcase" size={14} /> {[p.designation || p.specialty, p.employer].filter(Boolean).join(', ')}</p>
            <p><Icon name="pin" size={14} /> {p.city}{p.state ? `, ${p.state}` : ''}</p>
          </div>
          <MatchRing value={p.matchScore || 0} size={66} />
        </div>
        {p.matchReasons?.length > 0 && (
          <div className="swipe__reasons">
            {p.matchReasons.map((r) => <span key={r} className="tag tag--glass"><Icon name="check" size={12} stroke={2.6} /> {r}</span>)}
          </div>
        )}
        <button className="link-btn link-btn--light" onClick={() => navigate(`/profile/${p.profileId}`)}>
          View full profile <Icon name="arrowRight" size={14} />
        </button>
      </div>
    </motion.div>
  );
}

function SwipeDeck({ items }) {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(null);
  const { sendInterest, toggleShortlist } = useProfileActions();
  const current = items[index];

  const decide = (d) => {
    if (!current) return;
    setDir(d);
    if (d === 'like') sendInterest(current);
    if (d === 'star') toggleShortlist(current);
    setTimeout(() => setIndex((i) => i + 1), 10);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT') return;
      if (e.key === 'ArrowRight') decide('like');
      if (e.key === 'ArrowLeft') decide('skip');
      if (e.key === 'ArrowUp') decide('star');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!current) {
    return (
      <EmptyState
        icon="sparkle"
        title="You're all caught up!"
        text="New matches arrive every day. Meanwhile, explore with search or widen your preferences."
        action={
          <div className="row-actions">
            <button className="btn btn--ghost" onClick={() => setIndex(0)}><Icon name="undo" size={16} /> Start over</button>
            <Link to="/search" className="btn btn--primary">Search profiles</Link>
          </div>
        }
      />
    );
  }

  return (
    <div className="swipe">
      <div className="swipe__stack">
        <AnimatePresence custom={dir}>
          {items.slice(index, index + 2).reverse().map((p) => (
            <SwipeCard key={p.profileId} p={p} top={p.profileId === current.profileId} onDecide={decide} />
          ))}
        </AnimatePresence>
      </div>
      <div className="swipe__actions">
        <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.85 }} className="round-btn round-btn--skip" onClick={() => decide('skip')} aria-label="Skip">
          <Icon name="x" size={26} stroke={2.4} />
        </motion.button>
        <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.85 }} className="round-btn round-btn--star" onClick={() => decide('star')} aria-label="Shortlist">
          <Icon name="star" size={22} stroke={2.2} />
        </motion.button>
        <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.85 }} className="round-btn round-btn--like" onClick={() => decide('like')} aria-label="Send interest">
          <Icon name="heart" size={28} fill="currentColor" stroke={0} />
        </motion.button>
      </div>
      <p className="swipe__hint muted">
        {index + 1} of {items.length} · Drag the card, or use ← skip · ↑ shortlist · → interest
      </p>
    </div>
  );
}

const TABS = [
  { value: 'daily', label: 'Daily picks', icon: 'sparkle' },
  { value: 'newest', label: 'New', icon: 'gift' },
  { value: 'nearby', label: 'Near me', icon: 'pin' },
  { value: 'sameProfession', label: 'My profession', icon: 'steth' },
  { value: 'premium', label: 'Premium', icon: 'crown' },
];

export default function Matches() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'daily';
  const [view, setView] = useState('deck');
  const [data, setData] = useState(null);

  useEffect(() => {
    api('/profiles/discover').then(setData).catch(() => setData({}));
  }, []);

  const items = data?.[tab] || [];
  return (
    <Page>
      <div className="container">
        <PageHead eyebrow="Matches" title="People you'll want to meet" sub="Refreshed daily from your partner preferences.">
          <Link to="/me/preferences" className="btn btn--ghost btn--sm"><Icon name="sliders" size={16} /> Preferences</Link>
        </PageHead>
        <div className="toolbar">
          <Tabs id="matches" tabs={TABS} value={tab} onChange={(v) => setParams({ tab: v })} />
          {tab === 'daily' && (
            <div className="seg seg--sm">
              {[['deck', 'Swipe'], ['grid', 'Grid']].map(([k, l]) => (
                <button key={k} className={view === k ? 'is-active' : ''} onClick={() => setView(k)}>{l}</button>
              ))}
            </div>
          )}
        </div>
        {!data ? (
          <SkeletonGrid />
        ) : !items.length ? (
          <EmptyState icon="heart" title="No profiles here yet" text="Try another tab or widen your partner preferences." action={<Link to="/search" className="btn btn--primary">Search all profiles</Link>} />
        ) : tab === 'daily' && view === 'deck' ? (
          <SwipeDeck items={items} />
        ) : (
          <CardGrid key={tab} items={items} />
        )}
      </div>
    </Page>
  );
}
